<template>
    <div class="w-100 invoice-print">
        <invoice-print-table
            class="pl-2 pr-2"
            :columns="chargesColumnsDef"
            :values="chargesValues"
            emptyText="No Charges"
        />
        <invoice-print-table
            class="pl-2 pr-2"
            v-if="values.discounts.length > 0"
            :columns="discountsColumnDefs"
            :values="values.discounts"
            tableCaption="Discounts"
        />
        <invoice-print-table
            class="pl-2 pr-2"
            v-if="values.issuedPolicies.length > 0"
            :columns="issuedPoliciesColumnDefs"
            :values="values.issuedPolicies"
            tableCaption="Issued Policies"
        />
        <invoice-print-table
            class="pl-2 pr-2"
            v-if="values.otherDeposits.length > 0"
            :columns="otherDepositsColumnDefs"
            :values="values.otherDeposits"
            tableCaption="Other Deposits"
        />
        <invoice-print-table
            class="pl-2 pr-2"
            v-if="values.transactionHistory.length > 0"
            :columns="transactionColumnDefs"
            :values="values.transactionHistory"
            tableCaption="Transaction History"
        />
        <b-row
            v-for="item in values.techInfos"
            v-bind:key="item.id"
            class="ml-2 mr-2"
        >
            <b-col class="p-0">
                <invoice-tech-info-print
                    :initial-values="{
                        tech_info: item,
                        meta_reservation: values.reservation,
                        reservation_id: item.id,
                        meta_reservation_id: values.reservationId,
                    }"
                />
            </b-col>
        </b-row>
        <b-card
            title="Reservation Status"
            class="
                mb-2
                text-center text-light
                bg-sidebar
                reservation-status
                ml-2
                mr-2
            "
            no-body
        >
            <template #header>
                <b-row>
                    <b-col md="12"> Reservation Status </b-col>
                </b-row>
            </template>
            <b-card-body>
                <b-row>
                    <b-col
                        md="12"
                        class="bg-white reservation-state-container"
                        :class="{ 'airbnb-confirmed': isAirBnbConfirmed }"
                    >
                        <span
                            :class="
                                getReservationStatusIcon(
                                    values.reservationStatus
                                )
                            "
                        />
                        <span
                            class="state-string"
                            :class="
                                getReservationStatusColor(
                                    values.reservationStatus
                                )
                            "
                        >
                            {{
                                values.reservationStatus
                                    ? values.reservationStatus.state_string
                                    : ''
                            }}
                        </span>
                    </b-col>
                </b-row>
                <b-row class="balance-container align-items-center flex-nowrap">
                    <b-col md="6" class="align-left">
                        {{ getBalanceLabel(values) }}:
                        <div
                            v-if="balance.isRemaining"
                            class="balance-remaining"
                        >
                            {{ getOtherDepositsPaid(values) }}
                        </div>
                    </b-col>
                    <b-col md="6" class="align-right">
                        <span
                            class="balance-value"
                            :class="balance.balanceClass"
                            >{{ balance.balance }}</span
                        >
                    </b-col>
                </b-row>
            </b-card-body>

            <b-card-footer class="reservation-type-container">
                <b-row class="d-flex align-items-baseline flex-nowrap">
                    <b-col md="6" class="align-left">
                        <span class="align-left">Reservation Type</span>
                    </b-col>
                    <b-col md="6" class="align-right">
                        <span class="reservation-type">
                            <span v-if="values && values.stayType == 'OWNER'"
                                >Owner Stay</span
                            >
                            <span v-else>Guest Stay</span>
                        </span>
                    </b-col>
                </b-row>
            </b-card-footer>
        </b-card>
        <b-card
            title=""
            class="
                text-center
                confirmation-breakdown
                sidebar-header-card
                ml-2
                mr-2
                mt-1
            "
            no-body
        >
            <template #header>
                <font-awesome-icon
                    :icon="['far', 'square-check']"
                    fixed-width
                ></font-awesome-icon
                >&nbsp;&nbsp; Confirmation Breakdown
            </template>
            <div>
                <b-card-sub-title class="mb-2"
                    >Derived from
                    <a
                        title="Click to go to Reservation Deposit settings page."
                        href="/app/property-manager/reservation-deposit/"
                        >Reservation Deposit Rules</a
                    ></b-card-sub-title
                >
            </div>
            <b-card-body class="confirmation-breakdown-body">
                <b-card-title
                    >Confirmed = Res. Dep.<span
                        v-if="values && getShowOtherDeposits(values)"
                        >+ Other Dep.</span
                    ></b-card-title
                >
                <b-card-text>
                    <table class="confirmation-breakdown-body-table">
                        <tbody>
                            <tr class="bottom-border">
                                <td class="text-left">
                                    Res. Deposit
                                    <small
                                        v-if="
                                            values.confirmationBreakdown &&
                                            !values.confirmationBreakdown
                                                .deposit_is_flat
                                        "
                                        >({{
                                            values.confirmationBreakdown &&
                                            values.confirmationBreakdown
                                                .reservation_deposit
                                        }})</small
                                    >
                                </td>
                                <td class="text-right">
                                    {{
                                        values.confirmationBreakdown &&
                                        values.confirmationBreakdown
                                            .reservation_deposit_sum
                                    }}
                                </td>
                            </tr>
                            <tr
                                v-for="(item, i) in values.confirmationBreakdown
                                    .reservation_deposit_components"
                                :key="item.name + i"
                            >
                                <td class="text-left">
                                    <small>* {{ item.name }}</small>
                                </td>
                                <td class="text-right">
                                    <small>{{ item.value }}</small>
                                </td>
                            </tr>
                            <tr v-if="getShowOtherDeposits(values)">
                                <td class="text-left">Other Deposits</td>
                                <td class="text-right">
                                    <small>{{
                                        values.confirmationBreakdown
                                            .deposit_payment_sum
                                    }}</small>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </b-card-text>
            </b-card-body>
            <b-card-footer class="confirmation-breakdown-footer">
                <table class="confirmation-breakdown-footer-table">
                    <tbody>
                        <tr>
                            <td class="text-left">Total To Confirm</td>
                            <td class="text-right">
                                {{
                                    values.confirmationBreakdown
                                        .reservation_confirm_sum
                                }}
                            </td>
                        </tr>
                        <tr>
                            <td class="text-left">Paid Sum</td>
                            <td class="text-right">
                                {{ values.confirmationBreakdown.paid_sum }}
                            </td>
                        </tr>
                        <tr v-if="values.confirmationBreakdown.is_last_minute">
                            <td class="last-minute" colspan="2">
                                <small
                                    >This reservation requires FULL payment
                                    according to Universal Rules setup.</small
                                >
                            </td>
                        </tr>
                    </tbody>
                </table>
            </b-card-footer>
        </b-card>
        <b-card
            title=""
            class="text-center guest-details sidebar-header-card mr-2 ml-2"
        >
            <template #header>
                <font-awesome-icon
                    :icon="['fas', 'user']"
                    fixed-width
                ></font-awesome-icon
                >&nbsp;&nbsp; Guest Details
            </template>
            <b-card-body>
                <b-card-text>
                    <div class="guest-name-container">
                        <a class="guest-name">{{
                            values.reservation
                                ? values.reservation.user.short_name
                                : ''
                        }}</a>
                    </div>
                    <hr />
                    <table class="guest-details-body-table">
                        <tbody>
                            <tr>
                                <td class="text-left guest-details-label">
                                    <font-awesome-icon
                                        :icon="['fas', 'envelope']"
                                        fixed-width
                                    />
                                </td>
                                <td class="text-left">
                                    <a>{{
                                        values.reservation &&
                                        values.reservation.user.email
                                    }}</a>
                                </td>
                            </tr>
                            <tr
                                v-if="
                                    !isNil(values.reservation.user.phone.link_phone)
                                "
                            >
                                <td class="text-left guest-details-label">
                                    <font-awesome-icon
                                        :icon="['fas', 'phone-flip']"
                                        fixed-width
                                    />
                                </td>
                                <td class="text-left">
                                    <a
                                        :href="`tel:${values.reservation.user.phone.link_phone}`"
                                        >{{
                                            values.reservation.user.phone
                                                .display_phone
                                        }}</a
                                    >
                                </td>
                            </tr>
                            <tr v-if="values.reservation.user.address.address1">
                                <td class="text-left guest-details-label">
                                    <font-awesome-icon
                                        :icon="['fas', 'location-dot']"
                                        fixed-width
                                    />
                                </td>
                                <td class="text-left">
                                    {{ values.reservation.user.address.address1 }}
                                </td>
                            </tr>
                            <tr v-if="values.reservation.user.address.address2">
                                <td class="text-left"></td>
                                <td class="text-left">
                                    {{ values.reservation.user.address.address2 }}
                                </td>
                            </tr>
                            <tr
                                v-if="
                                    values.reservation.user.address.city ||
                                    values.reservation.user.address.state
                                "
                            >
                                <td class="text-left"></td>
                                <td class="text-left">
                                    {{ values.reservation.user.address.city }}
                                    <span
                                        v-if="
                                            values.reservation.user.address.city &&
                                            values.reservation.user.address.state
                                        "
                                        >,</span
                                    >
                                    {{
                                        values.reservation.user.address.state
                                            ? values.reservation.user.address.state
                                                  .code
                                            : ''
                                    }}
                                </td>
                            </tr>
                            <tr v-if="values.reservation.user.address.zip">
                                <td class="text-left"></td>
                                <td class="text-left">
                                    {{ values.reservation.user.address.zip }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </b-card-text>
            </b-card-body>
            <b-card-footer>
                <reservation-digital-signature-icon
                    :reservation="values.reservation"
                    v-if="!values.reservation.is_quote"
                />
                <reservation-has-services-icon
                    v-if="values.reservation.has_services"
                />
                <reservation-travel-insurance-icon
                    v-if="values.reservation.has_travel_insurance"
                />
                <reservation-guest-status-icon
                    :reservation="values.reservation"
                />
                <reservation-pets-icon v-if="values.reservation.has_pets" />
            </b-card-footer>
        </b-card>
    </div>
</template>

<script>
import isNil from 'lodash/isNil';
import { mapGetters } from 'vuex';
import ReservationHasServicesIcon from '../../../common/reservation-has-services-icon.vue';
import ReservationDigitalSignatureIcon from '../../../common/reservation-digital-signature-icon.vue';
import ReservationTravelInsuranceIcon from '../../../common/reservation-travel-insurance-icon.vue';
import ReservationGuestStatusIcon from '../../../common/reservation-guest-status-icon.vue';
import ReservationPetsIcon from '../../../common/reservation-pets-icon.vue';
import ReservationInvoiceStateIcon from '../reservation-invoice-state-icon.vue';
import InvoicePrintTable from './invoice-print-table.vue';
import InvoiceTechInfoPrint from './invoce-tech-info-print.vue';
import {
    getBalance,
    getOtherDepositsPaid,
    getShowOtherDeposits,
    getReservationStatusIcon,
    getReservationStatusColor,
    getBalanceLabel,
} from './utils.js';
import { toAmountDisplay } from '../../../../utils/numbers';
export default {
    components: {
        ReservationHasServicesIcon,
        ReservationDigitalSignatureIcon,
        ReservationTravelInsuranceIcon,
        ReservationGuestStatusIcon,
        ReservationPetsIcon,
        ReservationInvoiceStateIcon,
        InvoicePrintTable,
        InvoiceTechInfoPrint,
    },
    props: {
        invoiceValues: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            values: this.getInitialValues(),
        };
    },
    computed: {
        ...mapGetters('header', ['userCurrencySymbol']),

        balance() {
            return this.values && getBalance(this.values);
        },
        isAirBnbConfirmed() {
            return (
                this.values.reservationStatus.state === 'PAIDDEPOSIT' &&
                this.values.reservation.booking_method === 'AIRBNB'
            );
        },
        chargesValues() {
            return [...this.values.charges, ...this.values.chargesTotals];
        },
        chargesColumnsDef() {
            return [
                {
                    key: 'date',
                    label: 'Date',
                },
                {
                    key: 'description',
                    label: 'Description',
                },
                {
                    key: 'amount',
                    label: 'Amount',
                    formatter: (value, key, item) => {
                        return toAmountDisplay({
                            amount: value,
                            currency: this.userCurrencySymbol,
                        });
                    },
                },
                {
                    label: 'Discount',
                    key: 'discount',
                    resizable: false,
                    flex: 1,
                    hide: true,
                    formatter: (value, key, item) =>
                        item.is_cancelled ? '-' : item.discount,
                },
                {
                    key: 'tax',
                    label: 'Tax',
                },
                {
                    label: 'Net',
                    key: 'net',
                    formatter: (value, key, item) =>
                        item.is_cancelled ? '-' : item.net,
                },
                {
                    label: 'Tax',
                    key: 'tax',
                    formatter: (value, key, item) =>
                        item.is_cancelled ? '-' : item.tax,
                },
                {
                    key: 'total',
                    label: 'Total',
                    thClass: 'text-right',
                    tdClass: 'text-right',
                },
            ];
        },

        discountsColumnDefs() {
            return [
                {
                    label: 'Description',
                    key: 'description',
                },
                {
                    label: 'Coupon',
                    key: 'coupon',
                },
                {
                    lable: 'Amount',
                    key: 'amount',
                    thClass: 'text-right',
                    tdClass: 'text-right',
                },
            ];
        },
        otherDepositsColumnDefs() {
            return [
                {
                    label: 'Description',
                    key: 'description',
                },
                {
                    label: 'Date',
                    key: 'date',
                },
                {
                    label: 'Total',
                    key: 'total',
                    thClass: 'text-right',
                    tdClass: 'text-right',
                },
            ];
        },
        issuedPoliciesColumnDefs() {
            return [
                {
                    label: 'Policy ID',
                    key: 'external_policy_id',
                },
                {
                    label: 'Product Name',
                    key: 'description',
                },
            ];
        },
        transactionColumnDefs() {
            return [
                {
                    label: 'Date',
                    key: 'date',
                },
                {
                    label: 'Description',
                    key: 'description',
                },
                {
                    label: 'Debits(+) Credits(-)',
                    key: 'amount',
                },
                {
                    label: 'Charges Balance',
                    key: 'charges_balance',
                },
                {
                    label: 'Deposits Balance',
                    key: 'other_deposits_balance',
                    thClass: 'text-right',
                    tdClass: 'text-right',
                },
            ];
        },
    },
    methods: {
        isNil,
        getBalanceLabel,
        getOtherDepositsPaid,
        getShowOtherDeposits,
        getReservationStatusIcon,
        getReservationStatusColor,
        getInitialValues() {
            return {
                reservationStatus: this.invoiceValues?.reservation_status,
                reservationSchedule:
                    this.invoiceValues?.reservation_data.schedule,
                stayType: this.invoiceValues?.stay_type,
                confirmationBreakdown:
                    this.invoiceValues?.reservation_data.confirmation_breakdown,
                hasTravelInsurance: this.invoiceValues?.has_travel_insurance,
                hasPets: this.invoiceValues?.has_pets,
                reservation: this.invoiceValues,
                reservationId: this.invoiceValues?.id,
                bookingMethod: this.invoiceValues?.booking_method,
                charges: this.invoiceValues?.reservation_data.charges || [],
                chargesTotals:
                    this.invoiceValues?.reservation_data.charges_totals || [],
                discounts: this.invoiceValues?.reservation_data.discounts || [],
                issuedPolicies:
                    this.invoiceValues?.reservation_data.issued_policies || [],
                otherDeposits:
                    this.invoiceValues?.reservation_data.other_deposits || [],
                otherDepositsTotals:
                    this.invoiceValues?.reservation_data
                        .other_deposits_totals || [],
                transactionHistory:
                    this.invoiceValues?.reservation_data.transaction_history ||
                    [],
                techInfos:
                    this.invoiceValues?.reservation_data.tech_infos || [],
            };
        },
    },
    watch: {
        invoiceValues() {
            this.values = this.getInitialValues();
        },
    },
};
</script>

<style lang="scss" scoped>
@import '../reservation-invoice-sidebar.scss';
</style>